import { useSnackbar } from '../contexts/SnackBarContext';
import { useNavigate } from 'react-router-dom';
import { StorageKeys } from "../services/constants/StorageKeys";
import { storage } from "../services/storageService";
import { Routes } from '../routes/RouteConstants';

const useErrorHandler = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const handleError = (error, targetRouteBeforeRedirect) => {
    if (isServerUnavailable(error)) {
      showSnackbar("A szerveren jelenleg frissítés zajlik, kérlek, próbáld meg később.");
      return;
    }  

    // Redirect needed
    if (targetRouteBeforeRedirect) {
      if (isUnauthorizedError(error)) {
        handleUnauthorized(navigate, targetRouteBeforeRedirect);
        return;
      } 

      if (isProfileIncompleteError(error)) {
        handleProfileIncomplete(navigate, targetRouteBeforeRedirect);
        return;
      }
    }

    const publicMessage = error?.response?.data?.message;
    if (publicMessage) {
      showSnackbar(publicMessage);
      return;
    }  

    showSnackbar("Ismeretlen hiba történt, próbáld meg később vagy foglalj időpontot telefonon keresztül. (Error code: " + error.code + ")");
  };

  return { handleError };
};

// No backend behind nginx: during a deploy the proxy answers 502/503/504 itself, while an
// unreachable host surfaces as ERR_NETWORK.
const isServerUnavailable = (error) => {
  return error.code === "ERR_NETWORK" || [502, 503, 504].includes(error?.response?.status);
};

const isUnauthorizedError = (error) => {
  return error?.response?.status === 401;
};

const handleUnauthorized = (navigate, targetRouteBeforeRedirect) => {
  console.log("Request is unauthenticated, redirect needed to login route");

  storage.set(StorageKeys.IS_USER_AUTHENTICATED, false);
  storage.set(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT, targetRouteBeforeRedirect);

  navigate(Routes.LOGIN);
};

const isProfileIncompleteError = (error) => {
  return error?.response?.data?.code === "DEC-0006";
};

/**
 * DEC-0023: the booking was deleted after the page showing it was loaded — typically, cancelled.
 * Expected rather than an incident; the snackbar says so, and the page should catch up.
 */
export const isBookingGone = (error) => {
  return error?.response?.data?.code === "DEC-0023";
};

/**
 * DEC-0026: the booking is still there, but no longer in a state the action applies to — decided
 * already (an approve link clicked twice, or after a rejection), or closed (COMPLETED, EXPIRED,
 * REJECTED) while the page was open. A closed booking is final. The snackbar says so, and the page
 * should catch up to show the booking as it is now.
 */
export const isBookingStateChanged = (error) => {
  return error?.response?.data?.code === "DEC-0026";
};

/**
 * DEC-0024: the guest tried to cancel within the tenant's cancellation deadline — a page opened
 * before the deadline and clicked after it. The snackbar says so, and the page should catch up.
 */
export const isCancellationTooLate = (error) => {
  return error?.response?.data?.code === "DEC-0024";
};

/**
 * DEC-0025: the slot came within the tenant's booking deadline — a booking page loaded before the
 * deadline and clicked after it, or a confirmation page left open past it. The snackbar says so,
 * and the guest picks again from a fresh list.
 */
export const isBookingTooLate = (error) => {
  return error?.response?.data?.code === "DEC-0025";
};

/**
 * DEC-0029 to DEC-0032: a coupon the guest cannot use — unknown or not valid yet, expired, not for
 * this supply, or used already. The confirmation page shows these under the code field rather than
 * in a snackbar: there the guest sees which field they are about.
 */
const COUPON_REJECTED_CODES = ["DEC-0029", "DEC-0030", "DEC-0031", "DEC-0032"];

export const isCouponRejected = (error) => {
  return COUPON_REJECTED_CODES.includes(error?.response?.data?.code);
};

const handleProfileIncomplete = (navigate, targetRouteBeforeRedirect) => {
  console.log("Profile is incomplete, redirect needed to complete-registration route");

  storage.set(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT, targetRouteBeforeRedirect);

  navigate(Routes.COMPLETE_REGISTRATION);
};

export default useErrorHandler;
